import { createApp } from 'vue'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'
import App from './App.vue'
import router from './router'
import { i18n } from './i18n'
import { captureAttribution } from './lib/attribution'
import { initAnalytics, track } from './lib/analytics'
import { trackPlatformPageView } from './lib/platformAnalytics'
import { isStaleAssetError } from './lib/staleAssets'
import {
  isAssetPreloadLoadFailedEvent,
  isInjectedScriptReferenceError,
  isInjectedWebViewBridgeError,
  sentryExceptionMessages,
} from './lib/sentryFilters'
import './assets/index.css'

const app = createApp(App)

captureAttribution()
initAnalytics('dashboard')

// Sentry Error Tracking
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIRONMENT || 'development',
    integrations: [
      Sentry.browserTracingIntegration({ router }),
    ],
    tracesSampleRate: import.meta.env.VITE_ENVIRONMENT === 'production' ? 0.2 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      const exceptionMessages = sentryExceptionMessages(event)

      if (
        isStaleAssetError(hint.originalException) ||
        isStaleAssetError(event.message) ||
        isStaleAssetError(exceptionMessages)
      ) {
        return null
      }

      if (
        isInjectedWebViewBridgeError(hint.originalException) ||
        isInjectedWebViewBridgeError(event.message) ||
        isInjectedWebViewBridgeError(exceptionMessages) ||
        isInjectedScriptReferenceError(event) ||
        isAssetPreloadLoadFailedEvent(event, hint)
      ) {
        return null
      }

      return event
    },
  })
}

app.use(createPinia())
app.use(router)
app.use(i18n)

router.afterEach((to) => {
  const source = typeof to.name === 'string' ? to.name : 'unknown'
  const tourUuid = typeof to.params.uuid === 'string'
    ? to.params.uuid
    : typeof to.params.tourUuid === 'string'
      ? to.params.tourUuid
      : undefined

  track('dashboard_page_viewed', {
    source,
    tour_uuid: tourUuid,
  })

  trackPlatformPageView({
    path: to.fullPath,
    routeName: source,
  })
})

app.mount('#app')

// Initialize locale after mounting to avoid circular dependency issues
import { initializeLocale } from './i18n'
initializeLocale()
